import React, { useState } from "react";
import LoadingComponent from "../../components/common/LoadingComponent";
import { getIcon } from "../../constants/constants";
import { Icons } from "../../constants/icon-library";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useAssistantConnection } from "./useAssistantConnection";
import { useAssistantName } from "./useAssistantName";
import { useAssistantData } from "./useAssistantData";
// import { splitToolsByAvailability } from "./assistant.helper";
import {
  checkIntegrationSetup,
  // getPlatformDescription,
} from "./Assistants.utils";
import { useConnectionStatus } from "./useConnectionStatus";
import { syncUserData } from "../../features/user/userAPI";
import { toast } from "react-toastify";

function AssistantDetails() {
  const { assistantId, settingId } = useParams();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.user);
  const { deleteLoading } = useSelector((state) => state.setting);

  const { assistant, setting, setSetting, loading } = useAssistantData(
    assistantId,
    settingId,
  );
  const platformName = assistant?.platform?.type;
  // const platformDescription = getPlatformDescription(platformName);
  // const { availableTools, unavailableTools } = splitToolsByAvailability(tools);

  const needsSetup = checkIntegrationSetup(platformName, setting);

  const handleSetup = () => {
    navigate(`/apps/${assistant?.uuid}/config/setting/${settingId}`, {
      state: { setting },
    });
  };

  const handleConfigureWebhook = () => {
    navigate(`/apps/installed/${assistant.uuid}/setting/${settingId}/webhook`);
  };

  const isInstallPage = location.pathname.startsWith("/apps/install/");

  const {
    displayName,
    editedName,
    setEditedName,
    isEditing,
    setIsEditing,
    saveName,
    startEdit,
  } = useAssistantName({
    assistant,
    setting,
    assistantId,
    isInstallPage,
    setSetting,
  });

  const connectionStatus = useConnectionStatus({
    assistant,
    isInstallPage,
  });
  const { isDisconnectAction } = connectionStatus;
  const { handleConnectionButton } = useAssistantConnection({
    assistant,
    setting,
    setSetting,
    user,
    navigate,
    isDisconnectAction,
  });

  if (!loading && !assistant) {
    return (
      <div className="container my-10">
        <div className="alert">Assistant not found</div>
      </div>
    );
  }
  let iconSrc = getIcon(platformName);

  if (iconSrc === undefined) {
    iconSrc = assistant?.platform?.iconUrl;
  }

  const handleRefresh = async (setting) => {
    try {
      setIsRefreshing(true);
      await syncUserData({ setting });
      toast.success("Data successfully refreshed.");
    } catch (error) {
      console.error("Refresh failed:", error);
      toast.error("Failed to refreshed data.");
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleUpdate = async (assistant, setting) => {
    navigate(`/apps/${assistant}/config/setting/${settingId}/update`, {
      state: { setting },
    });
  };

  return (
    <div className="container my-1">
      <div className="card border-0 shadow-lg" style={{ marginTop: "60px" }}>
        <div className="card-body p-4">
          {loading ? (
            <LoadingComponent loading size={15} />
          ) : (
            <>
              <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between pb-3 border-bottom gap-2">
                <div className="d-flex align-items-center flex-grow-1 w-100 w-md-auto">
                  <div
                    className="rounded-circle bg-gradient d-flex justify-content-center align-items-center me-3 flex-shrink-0"
                    style={{
                      width: "60px",
                      height: "60px",
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      boxShadow: "0 4px 15px rgba(102,126,234,0.4)",
                    }}
                  >
                    <img
                      src={iconSrc}
                      alt={displayName}
                      style={{ width: "35px", height: "35px" }}
                    />
                  </div>

                  {!isEditing ? (
                    <div className="d-flex align-items-center flex-grow-1">
                      <h3 className="mb-0 me-2 text-truncate">{displayName}</h3>

                      {!isInstallPage && (
                        <button
                          className="btn btn-sm flex-shrink-0"
                          onClick={startEdit}
                          aria-label="Edit assistant name"
                        >
                          <i className={`wb ${Icons("edit")}`} />
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="d-flex align-items-center gap-2 flex-grow-1">
                      <input
                        type="text"
                        className="form-control bg-opacity-25 border-secondary"
                        value={editedName}
                        onChange={(e) => setEditedName(e.target.value)}
                        aria-label="Edit assistant name"
                      />
                      <button
                        className="btn btn-sm btn-success"
                        onClick={saveName}
                        aria-label="Save name"
                      >
                        <i className={`wb ${Icons("correct")}`} />
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => setIsEditing(false)}
                        aria-label="Cancel edit"
                      >
                        <i className={`wb ${Icons("cancel")}`} />
                      </button>
                    </div>
                  )}
                </div>

                <div className="d-flex align-items-center gap-2 flex-shrink-0">
                  {setting?.accessToken && (
                    <>
                      {needsSetup && (
                        <button
                          className="btn btn-setup px-4"
                          onClick={handleSetup}
                        >
                          <i
                            className={`wb ${Icons("resume")} me-1 align-middle`}
                          />
                          Setup
                        </button>
                      )}
                      {!needsSetup && (
                        <button
                          className="btn btn-setup px-4"
                          onClick={() =>
                            handleUpdate(assistantId, setting?.uuid)
                          }
                        >
                          <i
                            className={`wb ${Icons("constraints")} me-1 align-middle`}
                          />
                          Settings
                        </button>
                      )}
                      <button
                        className="btn btn-primary px-4"
                        onClick={() => handleRefresh(setting?.uuid)}
                        disabled={isRefreshing}
                      >
                        <i
                          className={`wb ${
                            isRefreshing ? Icons("load") : Icons("load")
                          } me-1 align-middle ${isRefreshing ? "fa-spin" : ""}`}
                        />
                        {isRefreshing ? "Refreshing..." : "Refresh Data"}
                      </button>
                      <button
                        className="btn btn-action px-4"
                        onClick={handleConfigureWebhook}
                        disabled={needsSetup}
                      >
                        <i
                          className="wb webconnect-broadcast
                           me-1 align-middle"
                          style={{ fontSize: "15px" }}
                        />
                        Configure Webhook
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* <div className="mb-3">
                <div
                  className="card border border-translucent shadow-sm mt-2"
                  style={{
                    background: "rgba(255,255,255,0.05)",
                    backdropFilter: "blur(10px)",
                    borderRadius: "14px",
                    padding: "20px",
                  }}
                >
                  <div className="card-body p-4">
                    <h5 className="fw-bold mb-1">Tools</h5>
                    <p className="mb-4" style={{ fontSize: "0.85rem" }}>
                      {platformDescription}
                    </p>

                    <small className="text-primary fw-semibold d-block mb-2">
                      Available
                    </small>
                    <div className="d-flex flex-wrap gap-2 mb-3 pb-2 border-opacity-25">
                      {availableTools.map((tool, index) => (
                        <span
                          key={index}
                          className="badge rounded-pill px-3 py-2 d-flex align-items-center gap-2"
                          style={{
                            background: "rgba(144, 255, 180, 0.15)",
                            border: "1px solid rgba(144, 255, 180, 0.45)",
                            color: "var(--phoenix-success-text-emphasis)",
                            fontSize: "0.82rem",
                          }}
                        >
                          {tool.name}
                        </span>
                      ))}
                    </div>

                    {unavailableTools?.length > 0 && (
                      <>
                        <small className="fw-semibold d-block mt-3 mb-2">
                          Unavailable
                        </small>

                        <div className="d-flex flex-wrap gap-2">
                          {unavailableTools.map((tool, index) => (
                            <span
                              key={index}
                              className="badge rounded-pill px-3 py-2 d-flex align-items-center gap-2"
                              style={{
                                background: "rgba(196, 97, 97, 0.15)",
                                border: "1px solid rgba(196, 97, 97, 0.45)",
                                color: "rgb(196, 97, 97)",
                                fontSize: "0.82rem",
                              }}
                            >
                              {tool.name}
                            </span>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div> */}

              {connectionStatus.showButton && (
                <div className="mb-3">
                  <div
                    className="card border border-translucent shadow-sm mt-2"
                    style={{
                      background: "rgba(255,255,255,0.05)",
                      backdropFilter: "blur(10px)",
                      borderRadius: "12px",
                    }}
                  >
                    <div className="card-body text-center py-3">
                      <div
                        className="rounded-circle d-inline-flex justify-content-center align-items-center mb-3"
                        style={{
                          width: "75px",
                          height: "75px",
                          background: "rgba(95, 212, 236, 0.25)",
                        }}
                      >
                        <i
                          className={`wb ${Icons("link")} text-info align-middle mt-2`}
                          style={{ fontSize: "1.9rem" }}
                        />
                      </div>

                      <h4 className="fw-bold mb-1">Platform Connection</h4>

                      <p className="mb-4" style={{ fontSize: "0.92rem" }}>
                        {isDisconnectAction
                          ? "Your platform is currently connected"
                          : "Connect your platform to get started"}
                      </p>

                      <button
                        className={`btn fw-semibold ${
                          isDisconnectAction ? "btn-danger" : "btn-primary"
                        }`}
                        style={{
                          padding: "12px 42px",
                          fontSize: "1rem",
                          borderRadius: "10px",
                        }}
                        onClick={handleConnectionButton}
                        disabled={deleteLoading}
                      >
                        {connectionStatus.buttonText}
                        <LoadingComponent
                          loading={deleteLoading}
                          size={7}
                          wrapperStyle={{
                            padding: 0,
                            paddingLeft: 3,
                            lineHeight: 0,
                            display: "inherit",
                            whiteSpace: "nowrap",
                          }}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AssistantDetails;
